import { useMemo, useState } from 'react';
import { ArrowDownUp, Gauge, LineChart, MapPin } from 'lucide-react';
import { useLocations } from '@/hooks/use-locations';
import { useLocationStore } from '@/stores/location-store';
import { isWarehouseType } from '@/lib/location-type';
import { stockPosition } from '@/lib/product-intelligence';
import { Segmented } from './intelligence-ui';
import { OverviewPanel } from './overview-panel';
import { PricingPanel } from './pricing-panel';
import { MovementPanel } from './movement-panel';

type View = 'overview' | 'pricing' | 'movement';

const VIEWS = [
  {
    value: 'overview' as const,
    label: (
      <>
        <Gauge className="h-3.5 w-3.5" aria-hidden="true" /> Overview
      </>
    ),
  },
  {
    value: 'pricing' as const,
    label: (
      <>
        <LineChart className="h-3.5 w-3.5" aria-hidden="true" /> Pricing
      </>
    ),
  },
  {
    value: 'movement' as const,
    label: (
      <>
        <ArrowDownUp className="h-3.5 w-3.5" aria-hidden="true" /> Movement
      </>
    ),
  },
];

function toNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Product Intelligence — the read-only analytics tab on the product page, for
 * owners and admins. The dashboard counterpart of the mobile app's Find
 * Product → Intelligence view: Overview (insights, stock and margin, stock by
 * location, best vendor), Pricing (vendor price trends, quoted vs paid) and
 * Movement (the stock ledger).
 *
 * Nothing here writes. It reads the product record the page already loaded,
 * the vendor price history the Vendors tab already uses, purchase-order paid
 * prices, and the stock-movement feed.
 */
export function IntelligenceTab({ product }: { product: any }) {
  const productId: string = product.id;
  const [view, setView] = useState<View>('overview');
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const { data: locData } = useLocations();

  const typeById = useMemo(
    () => new Map<string, string>((locData?.data ?? []).map((l: any) => [l.id, l.type])),
    [locData],
  );
  const isWarehouse = (id: string) => isWarehouseType(typeById.get(id));

  const position = useMemo(() => stockPosition(product, selectedLocationId || null), [product, selectedLocationId]);

  // The product's own locations rows are already limited by the server to the
  // locations this user may see.
  const stockRows = useMemo(
    () =>
      ((product.locations ?? []) as any[]).map((l) => ({
        locationId: l.locationId as string,
        locationName: (l.locationName as string) ?? '',
        qty: toNumber(l.inStockQty),
        threshold: toNumber(l.reorderThreshold),
      })),
    [product.locations],
  );

  // Movements are recorded per location. Follow the header's location; with
  // none selected, let the user pick one of the product's locations here.
  const [movementPick, setMovementPick] = useState('');
  const movementLocationId =
    selectedLocationId ||
    (stockRows.some((r) => r.locationId === movementPick) ? movementPick : (stockRows[0]?.locationId ?? ''));

  const payees: any[] = Array.isArray(product.payees) ? product.payees : [];
  const primaryVendorName: string | null = payees[0]?.payeeName ?? null;

  return (
    <div className="space-y-5" data-testid="product-intelligence">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented label="Intelligence view" options={VIEWS} value={view} onChange={setView} />
        <span className="text-xs text-muted-foreground">Read-only insights · owners and admins</span>
      </div>

      {view === 'overview' && (
        <OverviewPanel
          productId={productId}
          position={position}
          locationId={selectedLocationId}
          locationName={selectedLocationName}
          stockRows={stockRows}
          isWarehouse={isWarehouse}
          onSeePricing={() => setView('pricing')}
        />
      )}

      {view === 'pricing' && (
        <PricingPanel productId={productId} position={position} primaryVendorName={primaryVendorName} />
      )}

      {view === 'movement' &&
        (stockRows.length === 0 && !selectedLocationId ? (
          <p className="rounded-xl border border-border bg-white p-4 text-sm text-muted-foreground">
            This product isn&apos;t set up at any location yet, so it has no movement history.
          </p>
        ) : (
          <div className="space-y-4">
            {!selectedLocationId && (
              <label className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" aria-hidden="true" />
                Movement at
                <select
                  value={movementLocationId}
                  onChange={(e) => setMovementPick(e.target.value)}
                  className="h-8 rounded-lg border border-input bg-white px-2 text-sm text-foreground"
                >
                  {stockRows.map((r) => (
                    <option key={r.locationId} value={r.locationId}>
                      {r.locationName || 'Unnamed location'}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <MovementPanel productId={productId} locationId={movementLocationId} />
          </div>
        ))}
    </div>
  );
}
